import { ScrollableTableResponse, ScrollableTableRequest } from '@chilipiper/api-type-def'
import { RefObject, useEffect, useMemo, useState } from 'react'
import { useBottomScrollListener } from 'react-bottom-scroll-listener'

interface TableState<T> extends Omit<ScrollableTableResponse<T>, 'pageSize'> {
  filters: {
    [key: string]: any
    search?: string
  }
  loading: boolean
  meta: Record<string, any>
}

interface FetchResultsParams {
  filters: {
    [key: string]: any
    search?: string
  }
  isSilent?: boolean
  page: number
}

const defaultTableState: TableState<any> = {
  loading: true,
  results: [],
  page: 0,
  total: 0,
  filters: {
    search: '',
  },
  meta: {},
}

const RESULTS_PER_PAGE = 20

type FetchResultsType<T> = (params: ScrollableTableRequest) => Promise<ScrollableTableResponse<T>>

export interface UseScrollableTableParams<T> {
  defaultFilters?: Record<string, unknown>
  fetchResultsRequest: FetchResultsType<T>
  initialSearch?: string
  placeHolderResult: T
  resultsPerPage?: number
}

export const useScrollableTable = <T>(params: UseScrollableTableParams<T>) => {
  const {
    placeHolderResult,
    fetchResultsRequest,
    initialSearch = '',
    resultsPerPage = RESULTS_PER_PAGE,
    defaultFilters,
  } = params
  const initialFetchTable = () => fetchResults({ page: 0, filters: defaultFilters || {} })
  const initialTableState = useMemo(
    () => ({ ...defaultTableState, filters: { search: initialSearch } }),
    [initialSearch]
  )
  const [tableState, setTableState] = useState<TableState<T>>(initialTableState)
  const placeHolderResults = useMemo(
    () => new Array(10).fill(placeHolderResult),
    [placeHolderResult]
  )
  const { loading, results, page, total, filters } = tableState

  const fetchResults = (params: FetchResultsParams) => {
    const { page, isSilent, filters: paramsFilters } = params

    // when search is preset we should keep previous filters
    const newFilters = paramsFilters.search
      ? {
          ...filters,
          ...paramsFilters,
        }
      : // when there is no search we assume that we have all filters in `paramsFilters`.
        // It is essential not to merge in these case as we won't be able to distinguish filter deletion otherwise.
        {
          search: filters.search,
          ...paramsFilters,
        }

    setTableState(prevTableData => ({
      ...prevTableData,
      page,
      loading: !isSilent,
      results: page === 0 && !isSilent ? [] : results,
      filters: newFilters,
    }))

    return fetchResultsRequest({
      page,
      filters: newFilters,
      pageSize: resultsPerPage,
    }).then(res => {
      const { total = 0, ...rest } = res
      const newResults = res.result ? res.result : res.results // to be removed later when response type for all endpoints are the same
      const prevResults = page > 0 ? results : []
      setTableState(d => ({
        ...d,
        page,
        loading: false,
        results: [...prevResults, ...newResults],
        total,
        meta: rest,
      }))
    })
  }

  useEffect(function onMount() {
    initialFetchTable()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isMoreToShow =
    resultsPerPage * (page + 1) < total &&
    // todo remove this workaround once BE returns correct `total` for searched results
    results.length >= resultsPerPage

  const onBottomScroll = () => {
    if (loading) {
      return
    }

    if (!isMoreToShow) {
      return
    }
    fetchResults({ page: page + 1, filters })
  }

  const onSearch = (value: string) => {
    fetchResults({ page: 0, filters: { search: value } })
  }

  const onFiltersChange = (filters: object) => {
    fetchResults({ page: 0, filters })
  }

  const tableScrollableContainerRef = useBottomScrollListener(
    onBottomScroll,
    800,
    0
  ) as RefObject<HTMLDivElement>

  const resultsToShow =
    loading && page === 0
      ? placeHolderResults
      : [...results, ...(isMoreToShow ? placeHolderResults : [])]

  return {
    tableState,
    tableScrollableContainerRef,
    resultsToShow,
    onSearch,
    initialFetchTable,
    setTableState,
    fetchResults,
    onFiltersChange,
  }
}
